import React from 'react';
import { PLACEHOLDER_VIDEO } from '../utils/placeholders';

const VideoCarousel = ({ videoArticles, onVideoClick, onVideoModalClick, title = "Videos Buenos días" }) => {
  
  // Función para obtener thumbnail del video
  const getVideoThumbnail = (article) => {
    // Si tiene thumbnail específico del video
    if (article.videoThumbnail) {
      return article.videoThumbnail;
    }
    
    // Si es YouTube, generar thumbnail desde el videoId
    if (article.videoUrl && article.videoUrl.includes('youtube')) {
      const videoId = article.videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId[1]}/maxresdefault.jpg`;
      }
    }
    
    // Si es Vimeo, usar la imagen del artículo o un placeholder
    if (article.videoUrl && article.videoUrl.includes('vimeo')) {
      return article.imageUrl || article.image || PLACEHOLDER_VIDEO;
    }
    
    // Fallback a imagen del artículo o placeholder
    return article.imageUrl || article.image || PLACEHOLDER_VIDEO;
  };

  // Función para formatear duración del video (si está disponible)
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    // Si la duración viene en segundos
    if (typeof duration === 'number') {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    
    return duration;
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short'
      });
    } catch (error) {
      return '';
    }
  };

  if (!videoArticles || videoArticles.length === 0) {
    return null; // No mostrar la sección si no hay videos
  }

  return (
    <section className="mb-4 md:mb-6 lg:mb-8 px-1 md:px-6 lg:px-8 no-overflow">
      {/* Encabezado de sección */}
      <div className="flex justify-between items-center mb-4 px-4 md:px-0">
        <h2 className="responsive-title text-primary flex items-center gap-2">
          <span className="text-xl md:text-2xl lg:text-3xl">🍿</span>
          Vídeos destacados
        </h2>
        <button 
          onClick={() => {
            console.log('🍿 Navegando a Ver todos los vídeos');
            onVideoClick && onVideoClick('view-all');
          }}
          className="text-sm font-semibold hover:underline transition-colors"
          style={{ color: 'var(--primary-red)' }}
        >
          Ver todos
        </button>
      </div>

      {/* Carrousel horizontal de videos */}
      <div className="relative w-full overflow-hidden">
        <div 
          className="flex gap-3 md:gap-4 overflow-x-auto custom-scrollbar pb-4"
          style={{
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth'
          }}
        >
          {videoArticles.map((article, index) => (
            <div 
              key={article.id || index}
              className="modern-card stagger-animation animate-slide-up cursor-pointer flex-shrink-0 relative group"
              onClick={() => onVideoClick && onVideoClick(article)}
              style={{
                minWidth: '280px',
                maxWidth: '300px',
                width: '75vw',
                scrollSnapAlign: 'start'
              }}
            >
              {/* Thumbnail del video */}
              <div className="relative w-full h-40 rounded-xl overflow-hidden mb-4 bg-black">
                <img 
                  src={getVideoThumbnail(article)}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    // Sin esto el relleno vuelve a disparar onError y se reintenta en bucle
                    e.target.onerror = null;
                    e.target.src = PLACEHOLDER_VIDEO;
                  }}
                />
                
                {/* Overlay con play button */}
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-20 transition-all duration-300">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevenir que se abra el artículo
                      console.log(`🎥 Play clickeado para: ${article.title}`);
                      onVideoModalClick && onVideoModalClick(article, 'Buenos días');
                    }}
                    className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 hover:bg-opacity-100"
                  >
                    <div className="w-0 h-0 border-l-[12px] border-l-red-600 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ml-1"></div>
                  </button>
                </div>

                {/* Duración del video (si está disponible) */}
                {article.videoDuration && (
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    {formatDuration(article.videoDuration)}
                  </div>
                )}
              </div>

              {/* Contenido del video */}
              <div>
                <h3 className="text-base font-bold text-primary mb-2 line-clamp-2 leading-tight">
                  {article.title}
                </h3>
                
                {article.description && (
                  <p className="text-secondary text-sm line-clamp-2 leading-relaxed mb-3">
                    {article.description}
                  </p>
                )}

                {/* Metadatos del video */}
                <div className="flex items-center justify-between text-xs text-secondary">
                  <div className="flex items-center gap-2">
                    {article.date && (
                      <span className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded-full">
                        📅 {formatDate(article.date)}
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevenir que se abra el artículo
                        console.log(`🎥 Video clickeado para: ${article.title}`);
                        onVideoModalClick && onVideoModalClick(article, 'Buenos días');
                      }}
                      className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-full text-blue-600 font-medium hover:bg-blue-100 transition-colors cursor-pointer"
                    >
                      🎥 Video
                    </button>
                  </div>
                  
                  {article.author && article.author !== 'Agustinos' && (
                    <span className="flex items-center gap-1 text-xs opacity-75">
                      ✏️ {article.author}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VideoCarousel;
